import { useCallback, useMemo } from 'react';
import {
  Path,
  PathValue,
  SetValueConfig,
  UseFormReturn,
} from 'react-hook-form';
import { useTicketDocuments } from './use-ticket-documents';
import { useTicketDuplicateDialog } from './use-ticket-duplicate-dialog';
import {
  ExistingFilesRecord,
  markFileToDelete,
  TicketFormFilesField,
} from './ticket-form.utils';

export interface FileDeleteStateParams {
  fileId: string;
  markedForDelete: boolean;
}

export interface WatchedFilesFieldValue {
  files?: File[];
}

export interface UseTicketFormFilesParams<
  TData extends Record<string, unknown>,
> {
  ticketId?: string;
  formProps: UseFormReturn<TData>;
}

export const useTicketFormFiles = <TData extends Record<string, unknown>>({
  ticketId = '',
  formProps,
}: UseTicketFormFilesParams<TData>) => {
  const { documents: ticketDocuments } = useTicketDocuments(ticketId);
  const watchedFiles = formProps.watch(
    TicketFormFilesField.Files as Path<TData>
  ) as WatchedFilesFieldValue;
  const newFiles = useMemo(
    () => (watchedFiles?.files ?? []) as File[],
    [watchedFiles]
  );

  const setFilesValue = useCallback(
    (files: File[]) => {
      formProps.setValue(
        TicketFormFilesField.Files as Path<TData>,
        { files } as PathValue<TData, Path<TData>>,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [formProps]
  );

  const setDeleteState = useCallback(
    ({ fileId, markedForDelete }: FileDeleteStateParams) => {
      markFileToDelete(
        () =>
          formProps.getValues(
            TicketFormFilesField.ExistingFiles as Path<TData>
          ) as ExistingFilesRecord,
        (value: ExistingFilesRecord, options?: SetValueConfig) =>
          formProps.setValue(
            TicketFormFilesField.ExistingFiles as Path<TData>,
            value as PathValue<TData, Path<TData>>,
            options
          ),
        fileId,
        markedForDelete
      );
    },
    [formProps]
  );

  const {
    dialogOpen: open,
    dialogFiles: files,
    openUploadDialog,
    handleConfirm: onConfirm,
    handleCancel: onCancel,
    handleOpenChange: onOpenChange,
  } = useTicketDuplicateDialog({
    newFiles,
    existingDocuments: ticketDocuments,
    setDeleteState,
    setFilesValue,
  });

  const handleDuplicateFiles = useCallback(
    (
      duplicates: File[],
      _nonDuplicates: File[],
      onReplaceConfirm: () => void
    ) => openUploadDialog(duplicates, onReplaceConfirm),
    [openUploadDialog]
  );

  return {
    ticketDocuments,
    handleDuplicateFiles,
    DuplicateDialogProps: {
      open,
      files,
      onConfirm,
      onCancel,
      onOpenChange,
    },
  };
};
